/**
 * Tasks in **Board** — `docs/activity.md` §8.5, §8.6 (Board 19 A, B).
 *
 * A column per person picked (Unassigned first when it is picked), or — with
 * a task list open — a column per bucket. Not the list engine: no toolbar,
 * gear, ticks or bulk menu (do-not-substitute 24). A card is dragged to
 * give a task to somebody (or to move it between buckets); its due chip
 * opens the quick dates; a click opens the Task sheet.
 *
 * Aaron, Oct 23: a finished task stays in its own column, under a folded
 * *Completed* at the foot (there is no Done bucket, because buckets aren't
 * statuses), and only its circle ticks it; a bucket is dragged by its head
 * to a new place; every column runs to the bottom of the screen.
 *
 * Every number here is the server's (`GET /activity/task-board`): the
 * distinct open count, each column's open and late, *+ n more*.
 */
import { startTransition, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  boardFilterOf, emptyView, encodeView, firstNameOf, zonedLocal, type FilterCondition, type FilterGroup,
} from '@rowted/shared'
import { ConfirmDialog, Popover } from '../../components/overlays.js'
import { cn } from '../../lib/cn.js'
import { usePhone } from '../../lib/use-narrow.js'
import { activityRequest, peopleQuery } from '../api.js'
import { useActivity } from '../activity-context.js'
import { Glyph } from '../glyphs.js'
import { Face, UnassignedFace, type FacePerson } from '../faces.js'
import { openActivityNew } from '../calendar/overlays.js'
import { DoneCircle, DueChip, Faces, LinkChip, ListChip } from './parts.js'
import { QuickDueMenu, type DuePick } from './pickers.js'
import { openTaskSheet, tasksChanged, TASKS_CHANGED_EVENT } from './task-sheet.js'
import type { BoardAnswer, BoardCard, BoardColumn, TaskList } from './types.js'
import { doneWhen } from './words.js'
import { isTablet, useActivityLayout } from '../use-layout.js'
import { BoardSearch } from './board-search.js'
import { BoardFilter, boardFilterQuery } from './board-filter.js'
import { ChecklistBadge } from './task-checklist.js'
import { PriorityMark } from './task-priority.js'
import { TASK_CATEGORIES_EVENT, TaskCategoryChips } from './task-categories.js'

/** A card being dragged, and where it would land. */
interface Drag {
  card: BoardCard
  from: string
  x: number
  y: number
  /** The pointer's offset inside the card, so it does not jump. */
  dx: number
  dy: number
  width: number
  over: { column: string; index: number } | null
}

/** A shared task dropped on somebody else: *Give it to <name> only? · Add <name>* (§8.5). */
interface Asking { card: BoardCard; column: BoardColumn; index: number; beforeId: string | null }

const PRESS_MS = 300

export function TaskBoard({ list, onEditList }: { list: TaskList | null; onEditList: () => void }) {
  const { me, today, people, unassigned, state, change, reloadBadge } = useActivity()
  const phone = usePhone()
  const tablet = isTablet(useActivityLayout())
  const [, setParams] = useSearchParams()
  const timeZone = me.user.timeZone
  const [answer, setAnswer] = useState<BoardAnswer | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [tick, setTick] = useState(0)
  /*
   * The Board's filter (Aaron, Oct 26): this list's — or the people's — as it was last left on this
   * device, applied by the server by the List's own rule. The button says it is on; no chips.
   */
  const filterKey = list?.id ?? 'people'
  const filter = boardFilterOf(state.tasks.boardFilters, filterKey)
  const filterParam = boardFilterQuery(filter)
  const setFilter = (next: FilterGroup) => change({ tasks: { boardFilters: [{ key: filterKey, filter: next }] } })

  useEffect(() => {
    let live = true
    const timer = window.setTimeout(() => {
      const query = list
        ? `list=${list.id}`
        : `people=${peopleQuery(people)}&unassigned=${peopleQuery(unassigned)}`
      const filtered = filterParam ? `&filter=${encodeURIComponent(filterParam)}` : ''
      activityRequest<BoardAnswer>(`/activity/task-board?${query}${filtered}&q=${encodeURIComponent(q.trim())}`)
        .then((value) => { if (live) { setAnswer(value); setError(null) } })
        .catch((cause: unknown) => { if (live) setError(cause instanceof Error ? cause.message : 'The board could not load.') })
    }, q ? 200 : 0)
    return () => { live = false; window.clearTimeout(timer) }
  }, [list, people, unassigned, q, filterParam, tick])

  const reload = useCallback(() => setTick((n) => n + 1), [])
  useEffect(() => {
    window.addEventListener(TASKS_CHANGED_EVENT, reload)
    /* A category renamed, recoloured or removed: the cards' chips say so (§8.7). */
    window.addEventListener(TASK_CATEGORIES_EVENT, reload)
    return () => {
      window.removeEventListener(TASKS_CHANGED_EVENT, reload)
      window.removeEventListener(TASK_CATEGORIES_EVENT, reload)
    }
  }, [reload])

  const faces = useMemo(() => new Map<string, FacePerson>((answer?.people ?? []).map((p) => [p.id, p])), [answer])

  // ---- drag ---------------------------------------------------------------

  const [drag, setDrag] = useState<Drag | null>(null)
  const [asking, setAsking] = useState<Asking | null>(null)
  const columnsAt = useRef(new Map<string, HTMLDivElement>())
  const suppressClick = useRef(false)

  const columnOf = (key: string) => answer?.columns.find((column) => column.key === key) ?? null

  /** Which column, and before which card, a point is over. */
  const hit = (x: number, y: number, card: BoardCard): Drag['over'] => {
    for (const [key, node] of columnsAt.current) {
      const box = node.getBoundingClientRect()
      if (x < box.left || x > box.right || y < box.top || y > box.bottom) continue
      const column = columnOf(key)
      if (!column || !column.canDrop) return null
      /* The open cards only: a finished one under *Completed* is not a place to land. */
      const cards = [...node.querySelectorAll<HTMLElement>('[data-card]')]
        .filter((el) => el.dataset.card !== card.id && !el.closest('[data-completed]'))
      let index = cards.length
      for (let i = 0; i < cards.length; i += 1) {
        const r = cards[i]!.getBoundingClientRect()
        if (y < r.top + r.height / 2) { index = i; break }
      }
      return { column: key, index }
    }
    return null
  }

  const startDrag = (card: BoardCard, from: string, event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || phone) return
    const node = event.currentTarget
    const rect = node.getBoundingClientRect()
    const x0 = event.clientX
    const y0 = event.clientY
    const touch = event.pointerType !== 'mouse'
    let started = false
    let pressTimer: number | null = null
    const begin = (x: number, y: number) => {
      started = true
      setDrag({ card, from, x, y, dx: x0 - rect.left, dy: y0 - rect.top, width: rect.width, over: null })
    }
    if (touch) pressTimer = window.setTimeout(() => begin(x0, y0), PRESS_MS)
    const move = (next: PointerEvent) => {
      if (!started) {
        if (touch) {
          if (Math.hypot(next.clientX - x0, next.clientY - y0) > 8 && pressTimer) { window.clearTimeout(pressTimer); cleanup() }
          return
        }
        if (Math.hypot(next.clientX - x0, next.clientY - y0) < 4) return
        begin(next.clientX, next.clientY)
      }
      next.preventDefault()
      setDrag((current) => current && ({ ...current, x: next.clientX, y: next.clientY, over: hit(next.clientX, next.clientY, card) }))
    }
    const up = (next: PointerEvent) => {
      cleanup()
      if (!started) return
      suppressClick.current = true
      window.setTimeout(() => { suppressClick.current = false }, 0)
      const over = hit(next.clientX, next.clientY, card)
      setDrag(null)
      if (over) void drop(card, from, over)
    }
    const cleanup = () => {
      if (pressTimer) window.clearTimeout(pressTimer)
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
    }
    window.addEventListener('pointermove', move, { passive: false })
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
  }

  const send = async (card: BoardCard, column: BoardColumn, beforeId: string | null, mode?: 'only' | 'add') => {
    try {
      setError(null)
      await activityRequest('/activity/task-board/move', {
        method: 'POST',
        body: {
          taskId: card.id,
          ...(column.kind === 'person' ? { toPersonId: column.id } : {}),
          ...(column.kind === 'unassigned' ? { toPersonId: null, toUnassigned: column.id } : {}),
          ...(column.kind === 'bucket' ? { toBucketId: column.id } : {}),
          beforeId,
          ...(mode ? { mode } : {}),
        },
      })
      reloadBadge()
      tasksChanged()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That could not move.')
      reload()
    }
  }

  const drop = async (card: BoardCard, from: string, over: NonNullable<Drag['over']>) => {
    const column = columnOf(over.column)
    if (!column) return
    /* A finished card keeps its place under *Completed*; only another column moves it. */
    if (card.done && over.column === from) return
    const visible = column.cards.filter((c) => c.id !== card.id)
    const beforeId = visible[over.index]?.id ?? null
    if (over.column === from) {
      const at = column.cards.findIndex((c) => c.id === card.id)
      const after = column.cards[at + 1]?.id ?? null
      if (beforeId === after) return
    }
    /* A shared task onto somebody who is not on it asks first (§8.5). */
    if (column.kind === 'person' && card.people.length > 1 && !card.people.includes(column.id)) {
      setAsking({ card, column, index: over.index, beforeId })
      return
    }
    await send(card, column, beforeId)
  }

  // ---- a bucket by its head (Aaron, Oct 23) ---------------------------------

  const [bucketDrag, setBucketDrag] = useState<{ key: string; to: number } | null>(null)

  const bucketsOf = (columns: BoardColumn[]) => columns.filter((column) => column.kind === 'bucket')

  /** The buckets in the order a drag would leave them. */
  const reordered = (columns: BoardColumn[], key: string, to: number): BoardColumn[] => {
    const next = columns.filter((column) => column.key !== key)
    const moving = columns.find((column) => column.key === key)
    if (moving) next.splice(to, 0, moving)
    return next
  }

  const reorder = async (key: string, to: number) => {
    if (!answer || !list) return
    const next = reordered(bucketsOf(answer.columns), key, to)
    setAnswer({ ...answer, columns: next })
    try {
      setError(null)
      await activityRequest(`/task-lists/${list.id}/buckets/order`, { method: 'POST', body: { ids: next.map((column) => column.id) } })
      tasksChanged()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The buckets could not move.')
      reload()
    }
  }

  /**
   * Press a bucket's head and move: the other buckets make room as it passes
   * their middles (measured at the press, so the room it makes does not move
   * the target), and letting go saves the whole order. A touch holds first,
   * as a card does; *Move left* and *Move right* stay in the ⋯ for a keyboard.
   */
  const startBucketDrag = (column: BoardColumn, event: React.PointerEvent<HTMLElement>) => {
    if (event.button !== 0 || phone || !list?.canEdit || column.kind !== 'bucket' || !answer) return
    if ((event.target as HTMLElement).closest('button, input')) return
    const buckets = bucketsOf(answer.columns)
    const from = buckets.findIndex((one) => one.key === column.key)
    const middles = buckets.map((one) => {
      const box = columnsAt.current.get(one.key)?.getBoundingClientRect()
      return { key: one.key, mid: box ? box.left + box.width / 2 : 0 }
    })
    const target = (x: number) => middles.filter((slot) => slot.key !== column.key && slot.mid < x).length
    const x0 = event.clientX
    const y0 = event.clientY
    const touch = event.pointerType !== 'mouse'
    let started = false
    let pressTimer: number | null = null
    const begin = (x: number) => { started = true; setBucketDrag({ key: column.key, to: target(x) }) }
    if (touch) pressTimer = window.setTimeout(() => begin(x0), PRESS_MS)
    const move = (next: PointerEvent) => {
      if (!started) {
        if (touch) {
          if (Math.hypot(next.clientX - x0, next.clientY - y0) > 8 && pressTimer) { window.clearTimeout(pressTimer); cleanup() }
          return
        }
        if (Math.hypot(next.clientX - x0, next.clientY - y0) < 4) return
        begin(next.clientX)
      }
      next.preventDefault()
      const to = target(next.clientX)
      setBucketDrag((current) => (current && current.to !== to ? { ...current, to } : current))
    }
    const up = (next: PointerEvent) => {
      cleanup()
      if (!started) return
      setBucketDrag(null)
      const to = target(next.clientX)
      if (to !== from) void reorder(column.key, to)
    }
    const cleanup = () => {
      if (pressTimer) window.clearTimeout(pressTimer)
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
    }
    window.addEventListener('pointermove', move, { passive: false })
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
  }

  // ---- completed ------------------------------------------------------------

  /** Which columns have their *Completed* open — folded by default, every time (Aaron, Oct 23). */
  const [unfolded, setUnfolded] = useState<ReadonlySet<string>>(new Set())
  const fold = (key: string) => setUnfolded((current) => {
    const next = new Set(current)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
  })

  // ---- quick due ------------------------------------------------------------

  const [due, setDue] = useState<{ card: BoardCard; anchor: HTMLElement } | null>(null)
  const [lists, setLists] = useState<TaskList[]>([])
  useEffect(() => {
    activityRequest<{ items: TaskList[] }>('/task-lists').then((value) => setLists(value.items)).catch(() => setLists([]))
  }, [tick])
  const pickDue = async (card: BoardCard, pick: DuePick) => {
    try {
      await activityRequest(`/activity/tasks/${card.id}/due`, {
        method: 'POST', body: { date: pick.date, ...(pick.minutes !== undefined ? { minutes: pick.minutes } : {}) },
      })
      reloadBadge()
      tasksChanged()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That did not save.')
    }
  }

  // ---- adding ---------------------------------------------------------------

  const [adding, setAdding] = useState<string | null>(null)
  const addTo = async (column: BoardColumn, title: string) => {
    const words = title.trim()
    if (!words) { setAdding(null); return }
    try {
      await activityRequest('/activity/team-items', {
        method: 'POST',
        body: column.kind === 'person'
          ? { kind: 'task', title: words, participantIds: [column.id] }
          : { kind: 'task', title: words, participantIds: [], orgUnitId: column.id },
      })
      setAdding(null)
      reloadBadge()
      tasksChanged()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That task could not be made.')
    }
  }

  /**
   * *+ n more* opens that person's tasks in List (§8.5) — their open ones, or
   * under *Completed* every one they finished, however long ago (Aaron,
   * Oct 24). Unassigned's are the tasks with nobody on them in its branch.
   */
  const moreOf = (column: BoardColumn, done = false) => {
    const whose: FilterCondition[] = column.kind === 'unassigned'
      ? [{ field: 'peopleKey', op: 'is_null' }, { field: 'branch', op: 'in', value: [column.id] }]
      : [{ field: 'peopleKey', op: 'contains', value: column.id }]
    const view = {
      ...emptyView(),
      filter: {
        match: 'and' as const,
        /* With the Board's filter, so the List holds the very cards *+ n more* counted (Aaron, Oct 26). */
        conditions: [{ field: 'done', op: 'eq', value: done } satisfies FilterCondition, ...whose, ...(filter?.conditions ?? [])],
      },
      ...(done ? {} : { group: 'dueGroup' }),
      sort: [done ? { field: 'doneAt', direction: 'desc' as const } : { field: 'dueAt', direction: 'asc' as const }],
    }
    setParams((current) => {
      const next = new URLSearchParams(current)
      next.delete('saved')
      next.set('view', encodeView(view))
      return next
    }, { replace: true })
    /*
     * In the router's own transition, so List first draws with the view already in the address: on its
     * own this lands first, and the list opens on its default view before the address arrives.
     */
    startTransition(() => change({ tasks: { ...state.tasks, view: 'list', views: [{ key: list?.id ?? 'people', view: 'list' }] } }))
  }

  const newTask = () => openActivityNew({ kind: 'task', prefill: list ? { taskListId: list.id } : {} })

  // ---- drawing ----------------------------------------------------------------

  const cardView = (card: BoardCard, column: BoardColumn, lifted = false) => {
    const late = card.due?.tone === 'late' && !card.done
    const everyFace = Boolean(list)
    return (
      <div
        key={card.id}
        data-card={card.id}
        role="button"
        tabIndex={0}
        onPointerDown={lifted ? undefined : (event) => startDrag(card, column.key, event)}
        onClick={() => { if (!suppressClick.current && !lifted) openTaskSheet(card.id) }}
        onKeyDown={(event) => { if (event.key === 'Enter') openTaskSheet(card.id) }}
        className={cn('flex select-none flex-col gap-[7px] rounded-[10px] bg-surface px-[11px] py-2.5 text-left',
          /* The card whose quick dates are open wears the accent and its ring (Board 19 A). */
          due?.card.id === card.id && !lifted
            ? 'border-[1.5px] border-accent shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]' : 'border border-line',
          !lifted && due?.card.id !== card.id && 'cursor-grab hover:border-line-strong',
          lifted && 'pointer-events-none rotate-[2.5deg] shadow-[0_18px_40px_rgba(20,20,10,.22)] ring-2 ring-accent',
          drag && !lifted && drag.card.id === card.id && 'border-dashed opacity-35')}
      >
        {/* Its categories along the top, as Planner draws its labels (§8.7). */}
        {(card.categories?.length ?? 0) > 0 && <TaskCategoryChips categories={card.categories} max={3} />}
        <div className="flex items-start gap-2">
          <span className="pt-px">
            <DoneCircle done={card.done} late={late} size={17}
              onToggle={lifted ? undefined : () => void toggleDone(card)} />
          </span>
          <b className={cn('line-clamp-2 text-[12.5px] font-semibold leading-[1.35]', card.done && 'text-text-faint line-through')}>
            {card.title}
          </b>
        </div>
        {(card.due || card.chip || card.people.length > 1 || everyFace
          || (!card.done && ((card.priority ?? 'medium') !== 'medium' || card.checklist))) && (
          <div className="flex flex-wrap items-center gap-[5px]">
            {card.due && !card.done && (
              <DueChip words={card.due.words} tone={card.due.tone}
                onClick={lifted ? undefined : (event) => setDue({ card, anchor: event.currentTarget })} />
            )}
            {/* Planner's priority (Medium draws nothing) and checklist progress (§8.7). */}
            {!card.done && <PriorityMark priority={card.priority ?? 'medium'} />}
            {!card.done && <ChecklistBadge progress={card.checklist} />}
            {card.chip && !card.done && (card.chip.kind === 'list'
              ? <ListChip label={card.chip.label} />
              : <LinkChip type={card.chip.kind} label={card.chip.label} />)}
            <span className="flex-1" />
            {(everyFace || card.people.length > 1) && <Faces ids={card.people} byId={faces} size={20} />}
          </div>
        )}
        {card.done && card.doneByName && (
          <div className="text-[11px] font-semibold text-accent-dark">
            Done by {card.doneByName}{card.doneAt ? ` · ${doneWhen(card.doneAt, timeZone, today)}` : ''}
            {card.people.length === 2 ? ' — for both' : card.people.length > 2 ? ` — for all ${card.people.length}` : ''}
          </div>
        )}
      </div>
    )
  }

  const toggleDone = async (card: BoardCard) => {
    try {
      await activityRequest(card.done ? `/activities/${card.id}/reopen` : `/activities/${card.id}/done`,
        { method: 'POST', body: card.done ? {} : { done: true } })
      reloadBadge()
      tasksChanged()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That did not save.')
    }
  }

  const placeholder = (column: BoardColumn) => (
    <div className="flex h-16 shrink-0 items-center justify-center rounded-[10px] border-[1.5px] border-dashed border-accent bg-green-band-bg text-[11.5px] font-semibold text-accent">
      {column.kind === 'person' ? `Drop to give it to ${firstNameOf(column.title)}`
        : column.kind === 'unassigned' ? 'Drop to make it unassigned' : ''}
    </div>
  )

  const askBox = (ask: Asking) => {
    const first = firstNameOf(ask.column.title)
    return (
      <div className="flex flex-col gap-1.5 rounded-[10px] border border-accent-line bg-green-band-bg p-2.5 text-[12px]">
        <button type="button" onClick={() => { setAsking(null); void send(ask.card, ask.column, ask.beforeId, 'only') }}
          className="h-8 rounded-lg border border-accent bg-accent px-2.5 text-left font-semibold text-white">
          Give it to {first} only?
        </button>
        <button type="button" onClick={() => { setAsking(null); void send(ask.card, ask.column, ask.beforeId, 'add') }}
          className="h-8 rounded-lg border border-line bg-surface px-2.5 text-left font-semibold text-text">
          Add {first}
        </button>
        <button type="button" onClick={() => setAsking(null)} className="self-start px-1 text-[11.5px] font-semibold text-text-muted">
          Never mind
        </button>
      </div>
    )
  }

  const columnView = (column: BoardColumn) => {
    const over = drag?.over?.column === column.key ? drag.over : null
    const cards = column.cards
    const shown: ReactNode[] = []
    const moving = drag?.card.id
    const visible = cards.filter((card) => card.id !== moving)
    visible.forEach((card, index) => {
      if (over && over.index === index) shown.push(<div key="__drop">{placeholder(column)}</div>)
      if (asking && asking.column.key === column.key && asking.index === index) shown.push(<div key="__ask">{askBox(asking)}</div>)
      shown.push(cardView(card, column))
    })
    if (over && over.index >= visible.length) shown.push(<div key="__drop">{placeholder(column)}</div>)
    if (asking && asking.column.key === column.key && asking.index >= visible.length) shown.push(<div key="__ask">{askBox(asking)}</div>)
    /* The card being dragged keeps its place as a ghost in its own column. */
    if (moving && drag?.from === column.key) {
      const original = cards.find((card) => card.id === moving)
      if (original && !(over && over.column === column.key)) {
        const at = cards.findIndex((card) => card.id === moving)
        shown.splice(at, 0, <div key="__ghost" className="pointer-events-none">{cardView(original, column)}</div>)
      }
    }
    const bucket = column.kind === 'bucket'
    const completed = column.completed
    const open = unfolded.has(column.key)
    const lifted = bucketDrag?.key === column.key
    return (
      <div
        key={column.key}
        data-column={column.key}
        ref={(node) => { if (node) columnsAt.current.set(column.key, node); else columnsAt.current.delete(column.key) }}
        className={cn('flex shrink-0 flex-col gap-2 self-stretch rounded-xl border p-2.5',
          bucket ? 'w-[230px]' : 'w-[212px]', column.kind === 'unassigned' ? 'bg-hover' : 'bg-surface-alt',
          lifted ? 'border-dashed border-accent opacity-60' : 'border-line-faint')}
      >
        <ColumnHead column={column} list={list} faces={faces}
          onGrab={bucket && list?.canEdit && !phone ? (event) => startBucketDrag(column, event) : undefined}
          onAdd={() => (bucket ? openActivityNew({ kind: 'task', prefill: { taskListId: list?.id, bucketId: column.id } }) : setAdding(column.key))}
          onChanged={reload} onError={setError} />
        {(adding === column.key ? (
          <AddBox onDone={(title) => void addTo(column, title)} onCancel={() => setAdding(null)} />
        ) : (
          <button type="button"
            onClick={() => (bucket
              ? openActivityNew({ kind: 'task', prefill: { taskListId: list?.id, bucketId: column.id } })
              : setAdding(column.key))}
            className="flex h-[30px] items-center gap-1.5 rounded-lg px-2 text-[12px] font-semibold text-accent hover:bg-surface">
            <Glyph name="plus" size={13} />Add a task
          </button>
        ))}
        {shown}
        {column.more > 0 && (
          <button type="button" onClick={() => moreOf(column)}
            className="self-start px-1 py-0.5 text-[11.5px] font-semibold text-text-muted hover:text-text">
            + {column.more} more
          </button>
        )}
        {completed.count > 0 && (
          /* The finished ones, at the foot behind a chevron with their count (Aaron, Oct 23). */
          <div data-completed className="flex flex-col gap-2">
            <button type="button" aria-expanded={open} onClick={() => fold(column.key)}
              className="flex h-[30px] items-center gap-1.5 self-start rounded-lg px-1.5 text-[12px] font-semibold text-text-muted hover:bg-surface hover:text-text">
              <Glyph name={open ? 'chevronDown' : 'chevronRight'} size={13} />
              Completed
              <span className="rounded-full bg-surface px-[7px] py-px text-[11px] font-semibold text-text-muted">{completed.count}</span>
            </button>
            {open && completed.cards.map((card) => cardView(card, column))}
            {open && completed.count > completed.cards.length && (
              <button type="button" onClick={() => moreOf(column, true)}
                className="self-start px-1 py-0.5 text-[11.5px] font-semibold text-text-muted hover:text-text">
                + {completed.count - completed.cards.length} more
              </button>
            )}
          </div>
        )}
      </div>
    )
  }

  const columns = answer
    ? (bucketDrag ? reordered(bucketsOf(answer.columns), bucketDrag.key, bucketDrag.to) : answer.columns)
    : null

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-3.5">
      {list ? (
        <ListHead list={list} faces={faces} onEdit={onEditList} onNew={newTask}
          search={<>
            <BoardSearch value={q} onChange={setQ} height={34} />
            {/* The filter, beside the search and left of Edit list (Aaron, Oct 26). */}
            <BoardFilter listId={list.id} value={filter} onChange={setFilter} height={34} />
          </>} />
      ) : (
        /* On an iPad the bar's controls are 40 px and the search gives way first (§10.1). */
        <div className="flex min-w-0 items-center gap-2">
          {answer && <span className="shrink-0 whitespace-nowrap text-[12.5px] text-text-muted">{answer.open} open</span>}
          <span className="flex-1" />
          {/* The search, a button that opens where it stands (Aaron, Oct 25, note 1.9). */}
          <BoardSearch value={q} onChange={setQ} height={tablet ? 40 : 36} />
          {/* The filter, beside the search and left of New task (Aaron, Oct 26). */}
          <BoardFilter listId={null} value={filter} onChange={setFilter} height={tablet ? 40 : 36} />
          <button type="button" onClick={newTask}
            className={cn('inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[10px] border border-accent bg-accent px-3.5 text-[12.5px] font-semibold text-white hover:bg-accent-dark',
              tablet ? 'h-10' : 'h-9')}>
            <Glyph name="plus" size={15} />New task
          </button>
        </div>
      )}
      {error && <div className="text-[12.5px] text-danger">{error}</div>}
      {/* Every column runs to the bottom of the screen, however few cards it has (Aaron, Oct 23). */}
      <div className="flex min-h-[440px] flex-1 gap-3 overflow-x-auto pb-2">
        {columns ? columns.map(columnView) : <div className="p-2 text-[13px] text-text-muted">Loading…</div>}
        {list && answer && <AddBucket list={list} onAdded={reload} onError={setError} />}
      </div>
      {drag && (
        <div className="pointer-events-none fixed z-[80]" style={{ left: drag.x - drag.dx, top: drag.y - drag.dy, width: drag.width }}>
          {cardView(drag.card, columnOf(drag.from)!, true)}
        </div>
      )}
      {due && (
        <QuickDueMenu open onClose={() => setDue(null)} anchor={due.anchor} today={today}
          current={due.card.due ? dayOf(due.card.due.at, timeZone) : null}
          meets={lists.filter((l) => l.id === due.card.taskListId && l.meets).map((l) => l.meets!)}
          timeZone={timeZone} onPick={(pick) => void pickDue(due.card, pick)} />
      )}
    </div>
  )
}

const dayOf = (at: string, timeZone: string): string => zonedLocal(new Date(at), timeZone).date

/** An inline title box: Enter makes it, Esc leaves (§8.5). */
function AddBox({ onDone, onCancel }: { onDone: (title: string) => void; onCancel: () => void }) {
  const [title, setTitle] = useState('')
  return (
    <input
      autoFocus
      value={title}
      placeholder="Task name — Enter to add"
      onChange={(event) => setTitle(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onDone(title)
        if (event.key === 'Escape') onCancel()
      }}
      onBlur={() => { if (!title.trim()) onCancel() }}
      className="h-9 rounded-lg border border-accent bg-surface px-2.5 text-[12.5px] outline-none ring-[3px] ring-accent-ring"
    />
  )
}

function ColumnHead({
  column, list, faces, onAdd, onGrab, onChanged, onError,
}: {
  column: BoardColumn
  list: TaskList | null
  faces: Map<string, FacePerson>
  onAdd: () => void
  /** A bucket's head is what it is dragged by (Aaron, Oct 23). */
  onGrab?: (event: React.PointerEvent<HTMLDivElement>) => void
  onChanged: () => void
  onError: (message: string) => void
}) {
  const more = useRef<HTMLButtonElement>(null)
  const [menu, setMenu] = useState(false)
  const [renaming, setRenaming] = useState<string | null>(null)
  /* The name just saved, drawn until the board's next answer carries it, so the old one does not flash back. */
  const [named, setNamed] = useState<string | null>(null)
  useEffect(() => { setNamed(null) }, [column.title])
  /* One rename ends once: Enter, then the blur its input's leaving the page sends, must not save twice. */
  const ended = useRef(false)
  const [removing, setRemoving] = useState(false)

  const bucketCall = async (path: string, body: Record<string, unknown>, method: 'PATCH' | 'POST' = 'PATCH') => {
    try {
      await activityRequest(path, { method, body })
      onChanged()
      tasksChanged()
      return true
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'That did not save.')
      return false
    }
  }

  const startRename = () => { ended.current = false; setRenaming(named ?? column.title) }
  /**
   * Leaving the name box saves it — Enter, Tab or a click anywhere else
   * (Aaron, Oct 24: a name typed in the bucket and clicked away from was
   * dropped, because only Enter saved). Escape is the one way out unsaved.
   */
  const endRename = (save: boolean) => {
    if (ended.current || renaming === null) return
    ended.current = true
    const words = renaming.trim()
    setRenaming(null)
    if (!save || !words || words === (named ?? column.title)) return
    setNamed(words)
    void bucketCall(`/task-buckets/${column.id}`, { name: words }).then((ok) => { if (!ok) setNamed(null) })
  }

  const items: { key: string; label: string; onSelect: () => void; danger?: boolean }[] = column.kind === 'bucket'
    ? [
      { key: 'rename', label: 'Rename', onSelect: startRename },
      { key: 'left', label: 'Move left', onSelect: () => void bucketCall(`/task-buckets/${column.id}`, { move: 'left' }) },
      { key: 'right', label: 'Move right', onSelect: () => void bucketCall(`/task-buckets/${column.id}`, { move: 'right' }) },
      { key: 'remove', label: 'Remove', danger: true, onSelect: () => setRemoving(true) },
    ]
    : [{ key: 'add', label: 'Add a task', onSelect: onAdd }]

  const person = column.kind === 'person' ? faces.get(column.id) : undefined
  return (
    <div onPointerDown={onGrab} data-bucket-head={onGrab ? column.id : undefined}
      className={cn('group flex items-center gap-2 px-0.5 pt-0.5', onGrab && 'cursor-grab select-none')}>
      {column.kind === 'person' && person && <Face person={person} size={26} />}
      {column.kind === 'unassigned' && <UnassignedFace size={26} />}
      {renaming !== null ? (
        <input autoFocus value={renaming} aria-label="Bucket name" maxLength={40}
          onChange={(event) => setRenaming(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && renaming.trim()) endRename(true)
            if (event.key === 'Escape') endRename(false)
          }}
          onBlur={() => endRename(true)}
          className="h-7 min-w-0 flex-1 rounded-md border border-accent px-2 text-[13px] font-bold outline-none" />
      ) : column.kind === 'bucket' ? (
        <span className="flex min-w-0 items-center gap-2">
          <b className="truncate text-[13px]">{named ?? column.title}</b>
          <span className="rounded-full bg-surface px-[7px] py-px text-[11px] font-semibold text-text-muted">
            {column.open}
          </span>
        </span>
      ) : (
        <span className="flex min-w-0 flex-col">
          <b className="truncate whitespace-nowrap text-[13px]">{column.kind === 'unassigned' ? 'Unassigned' : column.title}</b>
          <span className="whitespace-nowrap text-[11px] font-semibold text-text-muted">
            {column.open} open{column.late > 0 && <> · <span className="text-danger">{column.late} late</span></>}
          </span>
        </span>
      )}
      <span className="flex-1" />
      {/* The head is the handle; the grip says so on a hover, and takes no room from the name. */}
      {onGrab && renaming === null && (
        <span aria-hidden className="text-text-faint opacity-0 group-hover:opacity-100"><Glyph name="grip" size={13} /></span>
      )}
      <button ref={more} type="button" aria-label="More" onClick={() => setMenu(true)} className="text-text-faint hover:text-text">
        <Glyph name="more" size={15} />
      </button>
      {menu && (
        <Popover open onClose={() => setMenu(false)} anchor={more.current} width={180} align="right" label="Column" className="rounded-xl py-1">
          {items.map((item) => (
            <button key={item.key} type="button" onClick={() => { setMenu(false); item.onSelect() }}
              className={cn('flex h-9 w-full items-center px-3 text-left text-[13px] font-medium hover:bg-hover', item.danger && 'text-danger')}>
              {item.label}
            </button>
          ))}
        </Popover>
      )}
      {removing && list && (
        <ConfirmDialog
          title={`Remove ${column.title}?`}
          body="Its tasks move to the list's first bucket. Nothing else changes."
          confirmLabel="Remove"
          onCancel={() => setRemoving(false)}
          onConfirm={() => { setRemoving(false); void bucketCall(`/task-buckets/${column.id}/remove`, {}, 'POST') }}
        />
      )}
    </div>
  )
}

/** A list's head (Board 19 B): the tile, the name, when it meets, its members, **Edit list** · **New task**. */
function ListHead({ list, faces, onEdit, onNew, search }: {
  list: TaskList; faces: Map<string, FacePerson>; onEdit: () => void; onNew: () => void
  /** The search, left of **Edit list** (Aaron, Oct 25, note 1.9). */
  search: ReactNode
}) {
  const { directory } = useActivity()
  const byId = new Map<string, FacePerson>([...faces, ...(directory?.people ?? []).map((p) => [p.id, p] as const)])
  const sub = list.meets
    ? `${list.meets.atWords}${list.meets.nextWords ? ` · next ${list.meets.nextWords}` : ''} · ${list.branchName}`
    : list.branchName
  return (
    <div className="flex items-center gap-2.5">
      <span className="inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg bg-surface text-accent">
        <Glyph name="board" size={14} />
      </span>
      <div className="flex min-w-0 flex-col">
        <b className="truncate text-[16px]">{list.name}</b>
        <span className="truncate text-[12px] text-text-muted">{sub}</span>
      </div>
      <Faces ids={list.memberIds} byId={byId} size={24} max={6} />
      <span className="flex-1" />
      {search}
      {list.canEdit && (
        <button type="button" onClick={onEdit}
          className="inline-flex h-[34px] items-center gap-1.5 whitespace-nowrap rounded-lg border border-line bg-surface px-3.5 text-[12.5px] font-semibold hover:bg-hover">
          <Glyph name="pencil" size={14} />Edit list
        </button>
      )}
      <button type="button" onClick={onNew}
        className="inline-flex h-[34px] items-center gap-1.5 whitespace-nowrap rounded-[10px] border border-accent bg-accent px-3.5 text-[12.5px] font-semibold text-white hover:bg-accent-dark">
        <Glyph name="plus" size={15} />New task
      </button>
    </div>
  )
}

/** The dashed *Add a bucket* column, last (Board 19 B). */
function AddBucket({ list, onAdded, onError }: { list: TaskList; onAdded: () => void; onError: (m: string) => void }) {
  const [name, setName] = useState<string | null>(null)
  const add = async () => {
    const words = name?.trim()
    if (!words) { setName(null); return }
    try {
      await activityRequest(`/task-lists/${list.id}/buckets`, { method: 'POST', body: { name: words } })
      setName(null)
      onAdded()
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'That bucket could not be added.')
    }
  }
  return (
    <div className="flex w-[150px] shrink-0 items-start justify-center self-stretch rounded-xl border-[1.5px] border-dashed border-line-strong px-2 pt-3.5">
      {name === null ? (
        <button type="button" onClick={() => setName('')}
          className="flex items-center gap-1.5 text-[12.5px] font-semibold text-accent">
          <Glyph name="plus" size={13} />Add a bucket
        </button>
      ) : (
        <input autoFocus value={name} onChange={(event) => setName(event.target.value)} placeholder="Bucket name"
          onKeyDown={(event) => { if (event.key === 'Enter') void add(); if (event.key === 'Escape') setName(null) }}
          onBlur={() => void add()}
          className="h-8 w-full rounded-lg border border-accent bg-surface px-2 text-[12.5px] outline-none" />
      )}
    </div>
  )
}
